<template>
    <div class="page-container custom-token-create">
        <!-- Page Header -->
        <div class="page-header-wrapper" :class="{ 'is-scrolled': isScrolled }">
            <div class="content-wrapper">
                <div class="page-header">
                    <div class="header-left">
                        <h1 class="page-title">Create Custom Access Token</h1>
                        <p class="page-subtitle">Configure token identity, assign user, and define permissions for the token.</p>
                    </div>
                    <div class="header-actions">
                        <button type="button" class="btn-cancel" @click="cancel">Cancel</button>
                        <button type="button" class="btn-save" @click="save" :disabled="isSaving || !isValid">
                            <span v-if="isSaving"><i class="fas fa-spinner fa-spin me-2"></i> Generating...</span>
                            <span v-else>Save & Generate Token</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-wrapper mt-4">
            <!-- Main Content Grid -->
            <div class="main-grid">
                <!-- Left Column: Form -->
                <div class="grid-column main-col">
                    <div class="content-card">
                        <!-- Identity Section -->
                        <div class="card-section">
                            <h3 class="section-title">Token Identity</h3>
                            <p class="section-desc">Basic configuration and assignment for this token.</p>

                            <div class="form-grid mt-3">
                                <div class="form-item">
                                    <label>Token Name <span class="required">*</span></label>
                                    <o-input-text v-model="formData.token_name" placeholder="e.g. PMS Integration Token" type="text" :class="{'border-danger': formData.token_name.length > 100}" />
                                    <small v-if="formData.token_name.length > 100" class="text-danger mt-1 d-block">Token name cannot exceed 100 characters</small>
                                </div>

                                <div class="form-item form-row">
                                    <div class="form-sub-item" style="flex: 1;">
                                        <label>
                                            Assign to User <span class="required">*</span>
                                            <span class="position-relative d-inline-flex align-items-center ms-1" @mouseenter="showUserAssignTip = true" @mouseleave="showUserAssignTip = false">
                                                <i class="fas fa-question-circle text-muted" style="cursor: help; font-size: 13px;"></i>
                                                <Transition name="fade">
                                                    <div v-if="showUserAssignTip" class="date-selection-tip user-assign-tip">
                                                        <i class="fas fa-info-circle mt-1 flex-shrink-0"></i>
                                                        <span>Only users assigned to a branch will appear here. If the user is missing, please ensure they are assigned to a branch and user group first.</span>
                                                    </div>
                                                </Transition>
                                            </span>
                                        </label>
                                        <v-select
                                            v-model="formData.user_id"
                                            :options="eligibleUsers"
                                            :reduce="user => user.id"
                                            :get-option-label="user => `${user.name} (${user.branch_name})`"
                                            placeholder="-- Search & Select Branch User --"
                                            class="modern-v-select"
                                        >
                                            <template #selected-option="option">
                                                {{ `${option.name} (${option.branch_name})`.length > 30 ? `${option.name} (${option.branch_name})`.substring(0, 30) + '..' : `${option.name} (${option.branch_name})` }}
                                            </template>
                                        </v-select>
                                    </div>
                                    <div class="form-sub-item" style="flex: 1;">
                                        <label>Expiration <span class="required">*</span></label>
                                        <div class="custom-select-wrapper">
                                            <select v-model="formData.expires_in" class="modern-select">
                                                <option value="30">30 Days</option>
                                                <option value="90">90 Days</option>
                                                <option value="365">1 Year</option>
                                                <option value="">Never Expires</option>
                                            </select>
                                            <i class="fas fa-chevron-down select-icon"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Permissions Section -->
                        <div class="card-section border-top pt-4 mt-4">
                            <h3 class="section-title">API Permissions Access <span class="required">*</span></h3>
                            <p class="section-desc mb-2">Select the allowed operations for each module.</p>

                            <div class="d-flex gap-2 flex-wrap mb-2 mt-3 align-items-center">
                                <button type="button" class="btn btn-action btn-bulk-read" @click="toggleBulkAction('read')">
                                    <i class="fas fa-book-reader me-1"></i> All Read
                                </button>
                                <button type="button" class="btn btn-action btn-bulk-create" @click="toggleBulkAction('create')">
                                    <i class="fas fa-plus-circle me-1"></i> All Create
                                </button>
                                <button type="button" class="btn btn-action btn-bulk-update" @click="toggleBulkAction('update')">
                                    <i class="fas fa-edit me-1"></i> All Update
                                </button>
                                <button type="button" class="btn btn-action btn-bulk-delete" @click="toggleBulkAction('delete')">
                                    <i class="fas fa-trash-alt me-1"></i> All Delete
                                </button>

                                <div class="ms-auto" v-if="formData.abilities.length > 0">
                                    <button type="button" class="btn btn-action btn-bulk-clear" @click="formData.abilities = []" title="Clear all permissions">
                                        <i class="fas fa-times me-1"></i> Clear All
                                    </button>
                                </div>
                            </div>

                            <div class="permissions-matrix mt-2">
                                <table class="matrix-table">
                                    <thead>
                                        <tr>
                                            <th>Module</th>
                                            <th class="text-center">Read</th>
                                            <th class="text-center">Create</th>
                                            <th class="text-center">Update</th>
                                            <th class="text-center">Delete</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="module in modules" :key="module.id">
                                            <td class="fw-medium">{{ module.name }}</td>
                                            <td class="text-center">
                                                <input type="checkbox" class="modern-checkbox" 
                                                    :value="`${module.id}:read`" v-model="formData.abilities">
                                            </td>
                                            <td class="text-center">
                                                <input type="checkbox" class="modern-checkbox" 
                                                    :value="`${module.id}:create`" v-model="formData.abilities">
                                            </td>
                                            <td class="text-center">
                                                <input type="checkbox" class="modern-checkbox" 
                                                    :value="`${module.id}:update`" v-model="formData.abilities">
                                            </td>
                                            <td class="text-center">
                                                <input type="checkbox" class="modern-checkbox" 
                                                    :value="`${module.id}:delete`" v-model="formData.abilities">
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Summary Preview -->
                <div class="grid-column side-col">
                    <div class="content-card summary-card modern-fintech-card p-0">
                        <div class="summary-header">
                            <div class="icon-circle">
                                <i class="fas fa-file-invoice"></i>
                            </div>
                            <h3 class="section-title mb-0" style="border: none; padding: 0;">Token Summary</h3>
                        </div>
                        
                        <div class="summary-body">
                            <div class="summary-item">
                                <div class="summary-label">
                                    <i class="fas fa-key text-muted me-2"></i> Token Name
                                </div>
                                <div class="summary-value fw-bold text-end">{{ formData.token_name || 'Not specified' }}</div>
                            </div>

                            <div class="summary-item">
                                <div class="summary-label">
                                    <i class="fas fa-user text-muted me-2"></i> Assigned User
                                </div>
                                <div class="summary-value text-end">
                                    <span v-if="selectedUser" class="fw-bold">{{ selectedUser.name }}</span>
                                    <span v-else class="text-muted fst-italic">No user selected</span>
                                </div>
                            </div>

                            <div class="summary-item">
                                <div class="summary-label">
                                    <i class="fas fa-building text-muted me-2"></i> Branch
                                </div>
                                <div class="summary-value text-end">
                                    <span v-if="selectedUser" class="fintech-badge">{{ selectedUser.branch_name }}</span>
                                    <span v-else class="text-muted">-</span>
                                </div>
                            </div>

                            <div class="summary-item">
                                <div class="summary-label">
                                    <i class="fas fa-clock text-muted me-2"></i> Expiration
                                </div>
                                <div class="summary-value text-end d-flex align-items-center justify-content-end gap-1">
                                    <template v-if="!formData.expires_in">
                                        <span class="fintech-badge bg-success-soft text-success">Never</span>
                                        <span class="position-relative d-inline-flex align-items-center ms-1" @mouseenter="showNeverTip = true" @mouseleave="showNeverTip = false">
                                            <i class="fas fa-question-circle text-muted" style="cursor: help; font-size: 13px;"></i>
                                            <Transition name="fade">
                                                <div v-if="showNeverTip" class="date-selection-tip never-expiration-tip">
                                                    <i class="fas fa-info-circle mt-1 flex-shrink-0"></i>
                                                    <span>Token expiration follows the maximum system lifespan of 10 years</span>
                                                </div>
                                            </Transition>
                                        </span>
                                    </template>
                                    <span v-else class="fintech-badge bg-primary-soft text-primary">{{ formData.expires_in }} Days</span>
                                </div>
                            </div>
                        </div>

                        <div class="summary-footer">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <span class="summary-label mb-0"><i class="fas fa-shield-alt text-muted me-2"></i> Permissions</span>
                                <span class="fintech-badge rounded-pill bg-primary text-white">{{ formData.abilities.length }}</span>
                            </div>
                            <div class="permissions-preview">
                                <span v-if="formData.abilities.length === 0" class="text-muted small fst-italic">No permissions selected</span>
                                <div class="d-flex flex-wrap gap-2">
                                    <span v-for="ability in groupedAbilities" :key="ability" class="fintech-badge permission-badge">
                                        {{ ability }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Credentials Modal -->
        <o-modal v-if="showModal" @close="finish" :isFooterVisible="true">
            <template v-slot:header>
                <div style="display:flex; justify-content:center; width:100%;">
                    <h4 style="margin:0; font-size:1.25rem; font-weight:600; color:#111827;">Token Generated Successfully!</h4>
                </div>
            </template>
            <template v-slot:body>
                <div class="text-center pt-2">
                    <div class="mb-3 d-flex justify-content-center">
                        <div class="checkmark-wrapper">
                            <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                <circle class="checkmark__circle" cx="26" cy="26" r="25" fill="none" />
                                <path class="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                            </svg>
                        </div>
                    </div>
                    <p class="mb-4">
                        Please copy this token now. For security reasons, <br><i class="fas fa-exclamation-triangle me-1 text-warning"></i><strong class="text-warning"> it will never be shown again.</strong>
                    </p>      
                    <div class="credential-box mb-4 text-start">
                        <div class="credential-label" style="font-size:12px; font-weight:600; color:#475569; margin-bottom:6px; text-transform:uppercase;">Personal Access Token</div>                     
                        <div class="token-display">
                            <input type="text" :value="generatedToken" class="form-control" readonly id="tokenInput" />
                            <button @click="copyToken" class="btn-copy">
                                <i class="fas" :class="copied ? 'fa-check text-success' : 'fa-copy'"></i>
                            </button>
                        </div>   
                        <div v-if="copied" class="text-success small mt-2"><i class="fas fa-check me-1"></i> Copied to clipboard</div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn-primary px-5" @click="finish">Done</button>
                    </div>
                </div>
            </template>
            <template v-slot:footer>
            </template>
        </o-modal>
    </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { OInputText, OModal } from '@/components/OmnihControl'
import { useToast } from '@/composables/useToast'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
    name: 'CustomTokenCreate',
    components: {
        OInputText,
        OModal,
        vSelect
    },
    setup() {
        const store = useStore()
        const router = useRouter()
        const { showToast } = useToast()

        const formData = reactive({
            token_name: '',
            user_id: '',
            expires_in: '30',
            abilities: []
        })

        const isSaving = ref(false)
        const showModal = ref(false)
        const generatedToken = ref('')
        const copied = ref(false)
        const isScrolled = ref(false)
        const showNeverTip = ref(false)
        const showUserAssignTip = ref(false)

        const eligibleUsers = computed(() => store.getters['CustomToken/eligibleUsers'] || [])

        const selectedUser = computed(() => {
            if (!formData.user_id) return null;
            return eligibleUsers.value.find(u => u.id === formData.user_id);
        })

        const isValid = computed(() => {
            return formData.token_name.trim().length > 0 && 
                   formData.token_name.trim().length <= 100 &&
                   formData.user_id && 
                   formData.abilities.length > 0
        })

        const groupedAbilities = computed(() => {
            const groups = {}
            formData.abilities.forEach(ability => {
                const parts = ability.split(':')
                if (parts.length === 2) {
                    const module = parts[0]
                    const action = parts[1]
                    if (!groups[module]) groups[module] = []
                    groups[module].push(action)
                }
            })
            return Object.entries(groups).map(([module, actions]) => {
                if (actions.length > 0) {
                    return `${module} : ${actions.join(', ')}`
                }
                return module
            })
        })

        const modules = [
            { id: 'merchant', name: 'Merchants' },
            { id: 'user', name: 'Users' },
            { id: 'product', name: 'Products' },
            { id: 'member', name: 'Members' },
            { id: 'point_plan', name: 'Point Plans' },
            { id: 'voucher', name: 'Vouchers' },
            { id: 'voucher_redeem', name: 'Voucher Redeem' },
            { id: 'benefit', name: 'Benefits' },
            { id: 'transaction', name: 'Transactions' },
            { id: 'transaction-report', name: 'Transaction Reports' },
        ]

        const handleScroll = () => {
            isScrolled.value = window.scrollY > 20;
        }

        function toggleBulkAction(action) {
            const allSelected = modules.every(m => formData.abilities.includes(`${m.id}:${action}`))
            
            if (allSelected) {
                // Remove all
                formData.abilities = formData.abilities.filter(a => !a.endsWith(`:${action}`))
            } else {
                // Add all
                modules.forEach(m => {
                    const ability = `${m.id}:${action}`
                    if (!formData.abilities.includes(ability)) {
                        formData.abilities.push(ability)
                    }
                })
            }
        }

        onMounted(() => {
            window.scrollTo(0, 0)
            window.addEventListener('scroll', handleScroll)
            store.dispatch('CustomToken/getEligibleUsers')
        })

        onUnmounted(() => {
            window.removeEventListener('scroll', handleScroll)
        })

        const cancel = () => {
            router.push({ path: '/connection-apps', query: { tab: 'custom-token' } })
        }

        const save = async () => {
            if (!isValid.value) return;
            
            isSaving.value = true;
            try {
                const res = await store.dispatch('CustomToken/create', { data: formData })
                generatedToken.value = res.data.data.access_token;
                showModal.value = true;
                showToast({
                    type: 'success',
                    title: 'Token Created',
                    message: 'Custom token successfully created.'
                })
            } catch (err) {
                console.error(err);
                showToast({
                    type: 'error',
                    title: 'Creation Failed',
                    message: err.response?.data?.message || 'Failed to create custom token.'
                })
            } finally {
                isSaving.value = false;
            }
        }

        const copyToken = () => {
            const input = document.getElementById('tokenInput')
            input.select()
            document.execCommand('copy')
            copied.value = true
            setTimeout(() => { copied.value = false }, 2000)
        }

        const finish = () => {
            showModal.value = false;
            router.push({ path: '/connection-apps', query: { tab: 'custom-token' } })
        }

        return {
            formData,
            eligibleUsers,
            selectedUser,
            isValid,
            groupedAbilities,
            modules,
            isSaving,
            showModal,
            generatedToken,
            copied,
            isScrolled,
            showNeverTip,
            showUserAssignTip,
            cancel,
            save,
            copyToken,
            finish,
            toggleBulkAction
        }
    }
}
</script>

<style scoped>
/* Page Layout */
.page-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f1f4f9;
    padding: 24px 40px;
}

.content-wrapper {
    max-width: 1240px;
    margin: 0 auto;
}

/* Page Header Wrapper */
.page-header-wrapper {
    position: sticky;
    top: 0;
    z-index: 99;
    margin: -24px -40px 16px -40px;
    padding: 24px 40px 0 40px;
    background: rgba(241, 244, 249, 0);
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0);
    backdrop-filter: blur(0px);
    -webkit-backdrop-filter: blur(0px);
    transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease, -webkit-backdrop-filter 0.3s ease;
}

.page-header-wrapper.is-scrolled {
    background: rgba(241, 244, 249, 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0.5);
}

/* Page Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    padding-bottom: 16px;
}

.page-title {
    font-size: 26px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
    margin: 0;
}

.page-subtitle {
    color: #64748b;
    font-size: 14px;
    margin: 4px 0 0 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

/* Buttons */
.btn-cancel {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 10px 24px;
    background: var(--primary-color, #F93E3E);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-save:hover {
    filter: brightness(0.9);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15);
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
}

/* Grid Layout */
.main-grid {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
    align-items: start;
}

@media (max-width: 992px) {
    .main-grid {
        grid-template-columns: 1fr;
    }
}

/* Content Cards */
.content-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    padding: 28px 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.card-section {
    margin-bottom: 32px;
}
.card-section:last-child {
    margin-bottom: 0;
}

.section-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.section-desc {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 20px;
}

/* Form Styling */
.form-grid {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.form-item label, .form-sub-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.form-row {
    display: flex;
    gap: 16px;
}

.form-sub-item {
    flex: 1;
    min-width: 0;
}

.required {
    color: #ef4444;
}

/* Inputs & Selects */
.modern-select {
    width: 100%;
    padding: 11px 14px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    background-color: #f8fafc !important;
    margin: 0 !important;
    transition: all 0.2s;
    appearance: none;
}

.modern-select:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 2px var(--secondary-color, #Fee9E9) !important;
    outline: none;
}

.custom-select-wrapper {
    position: relative;
}
.select-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    pointer-events: none;
    font-size: 0.8125rem;
}

.token-display {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}

.token-display input {
    background: #f8fafc;
}

.btn-copy {
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-copy:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
}

.btn-primary {
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    background: var(--primary-color, #F93E3E);
    color: white;
}

.btn-primary:hover {
    opacity: 0.9;
}

/* Action Buttons */
.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
}

.btn-bulk-read {
    background-color: #eff6ff;
    color: #1e40af;
}
.btn-bulk-read:hover {
    background-color: #dbeafe;
    color: #1e3a8a;
}
.btn-bulk-create {
    background-color: #f0fdf4;
    color: #166534;
}
.btn-bulk-create:hover {
    background-color: #dcfce7;
    color: #14532d;
}
.btn-bulk-update {
    background-color: #fffbeb;
    color: #b45309;
}
.btn-bulk-update:hover {
    background-color: #fef3c7;
    color: #92400e;
}
.btn-bulk-delete {
    background-color: #fef2f2;
    color: #991b1b;
}
.btn-bulk-delete:hover {
    background-color: #fee2e2;
    color: #7f1d1d;
}
.btn-bulk-clear {
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
}
.btn-bulk-clear:hover {
    background-color: #e2e8f0;
    color: #1e293b;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
}

.modal-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
}

:deep(input) {
    width: 100%;
    padding: 11px 14px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    background-color: #f8fafc !important;
    transition: all 0.2s;
}

:deep(input:focus) {
    border-color: var(--primary-color, #F93E3E) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 2px var(--secondary-color, #Fee9E9) !important;
    outline: none !important;
}

/* Reset generic input overrides inside vue-select */
:deep(.modern-v-select .vs__search),
:deep(.modern-v-select .vs__search:focus) {
    width: auto !important;
    flex: 1 !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    background-color: transparent !important;
    box-shadow: none !important;
    font-size: 14px !important;
    line-height: 28px !important;
}

/* Matrix Table */
.permissions-matrix {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
}

.matrix-table {
    width: 100%;
    border-collapse: collapse;
}

.matrix-table th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    color: #475569;
    padding: 14px 12px;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
}

.matrix-table td {
    padding: 14px 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    font-size: 14px;
    color: #334155;
}

.matrix-table tr:last-child td {
    border-bottom: none;
}

.modern-checkbox {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--primary-color, #F93E3E);
}

/* Modern Fintech Summary Card Override */
.modern-fintech-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 10px 40px -10px rgba(0,0,0,0.06);
    overflow: hidden;
    position: sticky;
    top: 90px;
    z-index: 10;
}

.summary-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-bottom: 1px solid #e2e8f0;
}

.icon-circle {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #f93e3e);
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    font-size: 16px;
}

.summary-body {
    padding: 24px;
}

.summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px dashed #e2e8f0;
}

.summary-item:last-child {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
}

.summary-label {
    font-size: 13px;
    color: #64748b;
    font-weight: 600;
    display: flex;
    align-items: center;
}

.summary-value {
    font-size: 14px;
    color: #0f172a;
    word-break: break-word;
}

.fintech-badge {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    background: #f1f5f9;
    color: #334155;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.bg-success-soft {
    background: #dcfce7 !important;
    color: #16a34a !important;
}

.bg-primary-soft {
    background: var(--secondary-color, rgba(249, 62, 62, 0.1)) !important;
    color: var(--primary-color, #f93e3e) !important;
}

.bg-primary {
    background: var(--primary-color, #f93e3e) !important;
    color: #ffffff !important;
}

.summary-footer {
    padding: 20px 24px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
}

.permission-badge {
    background: var(--secondary-color, rgba(249, 62, 62, 0.08));
    color: var(--primary-color, #f93e3e);
    border: 1px solid var(--primary-color, rgba(249, 62, 62, 0.2));
    font-weight: 500;
}


/* Animated Checkmark Modal */
.checkmark-wrapper {
    width: 64px;
    height: 64px;
}

.checkmark {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: block;
    stroke-width: 3;
    stroke: #fff;
    stroke-miterlimit: 10;
    box-shadow: inset 0px 0px 0px #16a34a;
    animation: fill .4s ease-in-out .4s forwards, scale .3s ease-in-out .9s both;
}

.checkmark__circle {
    stroke-dasharray: 166;
    stroke-dashoffset: 166;
    stroke-width: 2;
    stroke-miterlimit: 10;
    stroke: #16a34a;
    fill: none;
    animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark__check {
    transform-origin: 50% 50%;
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
    100% {
        stroke-dashoffset: 0;
    }
}

@keyframes scale {
    0%, 100% {
        transform: none;
    }
    50% {
        transform: scale3d(1.1, 1.1, 1);
    }
}

@keyframes fill {
    100% {
        box-shadow: inset 0px 0px 0px 50px #16a34a;
    }
}

/* Vue-Select Modern Overrides */
:deep(.modern-v-select) {
    --vs-border-color: #cbd5e1;
    --vs-border-radius: 8px;
    --vs-font-size: 14px;
    --vs-colors--light: #64748b;
}

:deep(.modern-v-select .vs__dropdown-toggle) {
    background-color: #f8fafc;
    border: 1px solid var(--vs-border-color);
    border-radius: var(--vs-border-radius);
    padding: 4px 8px;
    min-height: 42px;
    margin: 0 !important;
    transition: all 0.2s ease;
    align-items: center;
}

:deep(.modern-v-select .vs__selected-options) {
    flex-wrap: nowrap;
    overflow: hidden;
    padding: 0;
}

:deep(.modern-v-select .vs__selected) {
    margin: 0 !important;
    padding: 0 !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    line-height: 28px;
    flex-shrink: 0;
    max-width: calc(100% - 15px);
}

:deep(.modern-v-select.vs--open .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-toggle:focus-within) {
    background-color: #ffffff;
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}

:deep(.modern-v-select .vs__search::placeholder),
:deep(.modern-v-select .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-menu) {
    font-family: inherit;
    font-size: 14px;
    color: #334155;
}

:deep(.modern-v-select .vs__clear),
:deep(.modern-v-select .vs__open-indicator) {
    fill: #94a3b8;
}

:deep(.modern-v-select .vs__dropdown-menu) {
    border-color: #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    margin-top: 4px;
    padding: 4px 0;
}

:deep(.modern-v-select .vs__dropdown-option) {
    padding: 8px 16px;
    color: #334155;
}

:deep(.modern-v-select .vs__dropdown-option--highlight) {
    background: var(--primary-color, #F93E3E);
    color: #ffffff;
}

:deep(.modern-v-select .vs__selected) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    max-width: 100%;
}

.date-selection-tip.never-expiration-tip {
    left: auto;
    right: -10px;
    bottom: 100%;
    margin-bottom: 5px;
    width: max-content;
    max-width: 260px;
    white-space: normal;
    align-items: flex-start;
}

.date-selection-tip.never-expiration-tip::before {
    left: auto;
    right: 12px;
    bottom: -4px;
    border-width: 4px 4px 0 4px;
    border-color: #1e293b transparent transparent transparent;
}

.date-selection-tip.user-assign-tip {
    left: -10px;
    right: auto;
    bottom: 100%;
    margin-bottom: 5px;
    width: max-content;
    max-width: 260px;
    white-space: normal;
    align-items: flex-start;
    text-transform: none;
    letter-spacing: normal;
}

.date-selection-tip.user-assign-tip::before {
    left: 12px;
    right: auto;
    bottom: -4px;
    border-width: 4px 4px 0 4px;
    border-color: #1e293b transparent transparent transparent;
}
</style>
